import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {formatSauce} from '../burgerOptions';
const API_URL = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:4000/api" : "/api");

// Helper pour fetch avec token
async function fetchWithToken(url, token, options = {}) {
    const headers = options.headers ? {...options.headers} : {};
    if (token) headers["Authorization"] = "Bearer " + token;
    const res = await fetch(url, {...options, headers});
    if (res.status === 401) {
        // Déconnecte l'utilisateur si token expiré
        localStorage.removeItem("token");
        window.location.reload();
        return [];
    }
    if (res.status === 204) {
        return null;
    }
    return res.json();
}

export default function ChefPage() {
    const navigate = useNavigate();
    const [burgers, setBurgers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [token] = useState(() => localStorage.getItem("token") || "");

    useEffect(() => {
        if (!token) {
            navigate('/');
            return;
        }
        fetchBurgers();
    }, [token, navigate]);

    const fetchBurgers = async () => {
        try {
            const data = await fetchWithToken(`${API_URL}/burgers`, token);
            if (Array.isArray(data)) {
                // Filtrer les burgers avec des membres valides
                const validBurgers = data.filter(burger => burger.member && burger.member.pseudonym);
                
                // Trier : à faire en premier, puis finis
                const sortedBurgers = validBurgers.sort((a, b) => {
                    if (a.status === b.status) {
                        return a.member.pseudonym.localeCompare(b.member.pseudonym);
                    }
                    return a.status === 'a_faire' ? -1 : 1;
                });
                setBurgers(sortedBurgers);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des burgers:', error);
        } finally {
            setLoading(false);
        }
    };

    const toggleStatus = async (memberId, currentStatus) => {
        const newStatus = currentStatus === 'a_faire' ? 'fini' : 'a_faire';
        
        try {
            await fetchWithToken(`${API_URL}/burgers/${memberId}/status`, token, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: newStatus })
            });
            
            // Rafraîchir la liste
            await fetchBurgers();
        } catch (error) {
            console.error('Erreur lors du changement de statut:', error);
        }
    };

    const formatCooking = (cooking) => {
        const cookingMap = {
            'saignant': 'Saignant',
            'point': 'À point',
            'cuit': 'Cuit'
        };
        return cookingMap[cooking] || cooking;
    };

    const formatCheeseCooking = (cooking) => {
        return cooking === 'froid' ? 'Froid' : 'Cuit avec steak';
    };

    const renderBurgerCard = (burger) => {
        const isFinished = burger.status === 'fini';
        
        return (
            <div 
                key={burger._id}
                className={`bg-white rounded-lg shadow-md p-4 mb-4 border-l-4 transition-all duration-200 ${
                    isFinished 
                        ? 'border-green-500 opacity-60' 
                        : 'border-blue-500'
                }`}
            >
                {/* En-tête avec nom et bouton de statut */}
                <div className="flex justify-between items-center mb-3">
                    <h3 className={`text-lg font-bold ${
                        isFinished ? 'text-gray-500' : 'text-gray-800'
                    }`}>
                        {burger.member?.pseudonym || 'Membre inconnu'}
                    </h3>
                    <button
                        onClick={() => toggleStatus(burger.member?._id, burger.status)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                            isFinished
                                ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                                : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                    >
                        {isFinished ? 'À refaire' : 'Terminé'}
                    </button>
                </div>

                {/* Contenu du burger */}
                <div className={`space-y-3 text-sm ${
                    isFinished ? 'text-gray-500' : 'text-gray-700'
                }`}>
                    {/* Côté gauche - Base du burger */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1 md:space-y-2">
                            <div className="flex items-center gap-2">
                                <span className="text-lg">🥩</span>
                                <span className="font-semibold">Cuisson du steak:</span> {formatCooking(burger.steaks?.cooking || 'point')}
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <span className="text-lg">🧀</span>
                                <span className="font-semibold">Cheddar:</span> {burger.cheese?.count || 1} ({formatCheeseCooking(burger.cheese?.cooking || 'froid')})
                            </div>
                        </div>

                        {/* Côté droit - Sauces, oignons et suppléments */}
                        <div className="space-y-2">
                            {/* Oignons */}
                            <div className="flex items-start gap-2">
                                <span className="text-lg">🧅</span>
                                <div>
                                    <span className="font-semibold">Oignons:</span>
                                    {burger.onions && burger.onions.length > 0 ? (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {burger.onions.map(onion => (
                                                <span key={onion} className={`px-2 py-1 rounded text-xs ${
                                                    onion === 'cru' ? 'bg-blue-100 text-blue-800' : 
                                                    onion === 'caramelise' ? 'bg-orange-100 text-orange-800' : 
                                                    'bg-yellow-100 text-yellow-800'
                                                }`}>
                                                    {onion === 'cru' ? 'Cru' : onion === 'caramelise' ? 'Caramélisé' : 'Frit'}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-500 text-xs ml-2">Sans oignons</span>
                                    )}
                                </div>
                            </div>

                            {/* Sauces */}
                            <div className="flex items-start gap-2">
                                <span className="text-lg">🥫</span>
                                <div>
                                    <span className="font-semibold">Sauces:</span>
                                    {burger.sauces && burger.sauces.length > 0 ? (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {burger.sauces.map(sauce => (
                                                <span key={sauce} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                                                    {formatSauce(sauce)}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        <span className="text-gray-500 text-xs ml-2">Aucune sauce</span>
                                    )}
                                </div>
                            </div>

                            {/* Suppléments */}
                            <div className="flex items-start gap-2">
                                <span className="text-lg">➕</span>
                                <div>
                                    <span className="font-semibold">Suppléments:</span>
                                    {(burger.tomato || burger.lettuce || burger.pickles || burger.bacon) ? (
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {burger.tomato && (
                                                <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                                                    🍅 Tomate
                                                </span>
                                            )}
                                            {burger.lettuce && (
                                                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                                    🥬 Salade
                                                </span>
                                            )}
                                            {burger.pickles && (
                                                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                                                    🥒 Pickles
                                                </span>
                                            )}
                                            {burger.bacon && (
                                                <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-xs">
                                                    🥓 Bacon
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="text-gray-500 text-xs ml-2">Aucun supplément</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Commentaire */}
                {burger.commentaire && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-start gap-2">
                            <span className="text-lg">💬</span>
                            <div>
                                <span className="font-semibold text-gray-700">Commentaire:</span>
                                <div className="mt-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                    <p className="text-orange-800 text-sm whitespace-pre-wrap">{burger.commentaire}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Indicateur de statut */}
                {isFinished && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-green-600 font-semibold text-sm">
                            ✅ Terminé
                        </span>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex justify-center items-center">
                <div className="bg-white rounded-xl p-8 shadow-xl">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Chargement des commandes...</p>
                    </div>
                </div>
            </div>
        );
    }

    const pendingBurgers = burgers.filter(b => b.status === 'a_faire');
    const finishedBurgers = burgers.filter(b => b.status === 'fini');

    return (
        <div className="min-h-screen bg-gray-100">
            {/* En-tête */}
            <div className="bg-white shadow-sm border-b px-4 py-3">
                <div className="flex justify-between items-center max-w-4xl mx-auto">
                    <h1 className="text-xl font-bold text-gray-800">
                        🍳 Interface Cuisinier
                    </h1>
                    <button
                        onClick={() => navigate('/')}
                        className="text-gray-500 hover:text-gray-700 text-2xl"
                    >
                        ✕
                    </button>
                </div>
            </div>

            {/* Contenu */}
            <div className="max-w-4xl mx-auto p-4">
                {/* Statistiques */}
                <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
                    <div className="grid grid-cols-2 gap-4 text-center">
                        <div>
                            <div className="text-2xl font-bold text-blue-600">{pendingBurgers.length}</div>
                            <div className="text-sm text-gray-600">À faire</div>
                        </div>
                        <div>
                            <div className="text-2xl font-bold text-green-600">{finishedBurgers.length}</div>
                            <div className="text-sm text-gray-600">Terminés</div>
                        </div>
                    </div>
                </div>

                {/* Liste des burgers */}
                <div className="space-y-4">
                    {burgers.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-2">🍔</div>
                            <p>Aucune commande pour le moment</p>
                        </div>
                    ) : (
                        <>
                            {/* Burgers à faire */}
                            {pendingBurgers.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-800 mb-3">
                                        À préparer ({pendingBurgers.length})
                                    </h2>
                                    {pendingBurgers.map(renderBurgerCard)}
                                </div>
                            )}

                            {/* Burgers terminés */}
                            {finishedBurgers.length > 0 && (
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-600 mb-3">
                                        Terminés ({finishedBurgers.length})
                                    </h2>
                                    {finishedBurgers.map(renderBurgerCard)}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
} 
